/**
 * Typed Clerk webhook event catalog. Conforms to the shared
 * `InboundEventMetadata` shape so the same Settings panel renders
 * every inbound provider's events with no per-provider UI code.
 *
 * Hard schema invariant (asserted in `tests/clerk-supported-events.test.ts`):
 * every entry's `payloadSchema.fields` MUST include
 * `{ key: 'email', type: 'string', required: true }`. Trigger-time
 * suppression and per-recipient send paths all key on this field —
 * runtime never has to handle a missing email column.
 *
 * For events that don't naturally carry an email
 * (`organization.created`), the inbound extractor
 * (`extract-payload.ts`) emits a synthetic fallback
 * `unknown+<seed>@<orgId>.brew.local` so the schema invariant holds.
 * Documented in each affected event's `description`.
 *
 * Catalog scope: 13 events curated for email-marketing automation
 * use cases — auth lifecycle (user.created), team workflows
 * (organization.created,
 * organizationInvitation.{created,accepted,revoked},
 * organizationMembership.created), commerce
 * (paymentAttempt.created, subscription.created,
 * subscriptionItem.{freeTrialEnding,pastDue,canceled,abandoned}),
 * and pre-launch capture (waitlistEntry.created).
 *
 * File layout: this module is the public surface. Field-set
 * definitions + the per-event metadata catalog live in
 * `event-metadata.ts`; the standalone event-type union lives in
 * `event-types.ts`. Both are re-exported below for back-compat
 * imports.
 */

import type { InboundEventCategory } from '@/lib/integrations/inbound/types'
import {
  CLERK_EVENT_METADATA,
  type ClerkSupportedEventMetadata,
} from './event-metadata'
import {
  SUPPORTED_CLERK_EVENT_SET,
  SUPPORTED_CLERK_EVENTS,
  type SupportedClerkEventType,
} from './event-types'

export type { ClerkSupportedEventMetadata, SupportedClerkEventType }
export { CLERK_EVENT_METADATA, SUPPORTED_CLERK_EVENTS }

export type ClerkEventCategoryId =
  | 'user'
  | 'organization'
  | 'billing'
  | 'waitlist'

export type ClerkEventConfigState = {
  eventType: SupportedClerkEventType
  enabled: boolean
  /**
   * Legacy paired toggle — kept on the type for back-compat with
   * existing call sites. The column is vestigial: after the one-switch
   * refactor (May 2026, see `docs/INTEGRATION_TRIGGERS_REFACTOR.md`)
   * triggers no longer have a status field at all. The fire decision
   * lives entirely on the bound automation's `published` flag.
   */
  triggerLoop: boolean
}

export const CLERK_EVENT_CATEGORIES: ReadonlyArray<InboundEventCategory> = [
  { id: 'user', label: 'User' },
  { id: 'organization', label: 'Organization' },
  { id: 'billing', label: 'Billing' },
  { id: 'waitlist', label: 'Waitlist' },
]

export const CLERK_EVENT_METADATA_BY_TYPE = new Map<
  SupportedClerkEventType,
  ClerkSupportedEventMetadata
>(CLERK_EVENT_METADATA.map((entry) => [entry.eventType, entry]))

export function isSupportedClerkEvent(
  eventType: string
): eventType is SupportedClerkEventType {
  return SUPPORTED_CLERK_EVENT_SET.has(eventType)
}

function getDefaultClerkEventConfigState(
  eventType: SupportedClerkEventType
): ClerkEventConfigState {
  return {
    eventType,
    enabled: false,
    // Vestigial — the value is no longer consulted at runtime
    // (post the May 2026 one-switch refactor, fire is gated solely
    // by `automation.published`). Kept on the type so existing
    // typed call sites compile.
    triggerLoop: false,
  }
}

export function getDefaultClerkEventConfigStates(): Array<ClerkEventConfigState> {
  return SUPPORTED_CLERK_EVENTS.map(getDefaultClerkEventConfigState)
}

export function groupClerkEventMetadataByCategory(): Record<
  ClerkEventCategoryId,
  Array<ClerkSupportedEventMetadata>
> {
  const buckets: Record<
    ClerkEventCategoryId,
    Array<ClerkSupportedEventMetadata>
  > = {
    user: [],
    organization: [],
    billing: [],
    waitlist: [],
  }
  for (const item of CLERK_EVENT_METADATA) {
    const bucket = buckets[item.category as ClerkEventCategoryId]
    if (bucket) {
      bucket.push(item)
    }
  }
  return buckets
}
